import { Routes, Route, Navigate, useLocation } from "react-router"
import { useAuth } from "@/lib/auth"
import { PlatformShell } from "@/components/PlatformShell"
import { OpsShell } from "@/components/OpsShell"
import { PropertyWorkspaceLayout } from "@/components/PropertyWorkspaceLayout"
import { ValidatePropertyAccess } from "@/components/ValidatePropertyAccess"
import { LegacyRouteRedirect } from "@/components/LegacyRouteRedirect"
import { LoginPage } from "@/pages/LoginPage"
import { SignUpPage } from "@/pages/auth/SignUpPage"
import { CheckInboxPage } from "@/pages/auth/CheckInboxPage"
import { EmailConfirmedPage } from "@/pages/auth/EmailConfirmedPage"
import { ActivateAccountPage } from "@/pages/auth/ActivateAccountPage"
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage"
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage"
import { OnboardingPage } from "@/pages/OnboardingPage"
import { OverviewSurface } from "@/pages/overview/OverviewSurface"
import { SettingsPage } from "@/pages/SettingsPage"
import { GuidebookPage } from "@/public/guidebook/GuidebookPage"
import { GuestGuidebookPage } from "@/public/guidebook/GuestGuidebookPage"
import { TvDisplayPage } from "@/public/tv/TvDisplayPage"
import { ReviewGuidePage } from "@/public/eink/ReviewGuidePage"
import { PortfolioPage } from "@/pages/portfolio/PortfolioPage"
import { GuestExperiencePage } from "@/pages/guest-experience/GuestExperiencePage"
import { GuidebookTab } from "@/pages/guest-experience/GuidebookTab"
import { InRoomDisplayTab } from "@/pages/guest-experience/InRoomDisplayTab"
import { RecommendationsTab } from "@/pages/guest-experience/RecommendationsTab"
import { HouseRulesTab } from "@/pages/guest-experience/HouseRulesTab"
import { AppShell } from "@/components/shell/AppShell"
import { PlatformOverviewPage } from "@/pages/platform/PlatformOverviewPage"
import { PlatformUsersPage } from "@/pages/platform/PlatformUsersPage"
import { PlatformPropertiesPage } from "@/pages/platform/PlatformPropertiesPage"
import { OpsAdminPage } from "@/pages/OpsAdminPage"
import { OpsAiRoutingPage } from "@/pages/OpsAiRoutingPage"
import { OpsUsersPage } from "@/pages/OpsUsersPage"
import { OpsCostBreakdownPage } from "@/pages/OpsCostBreakdownPage"
import { RevenuePage } from "@/pages/revenue/RevenuePage"
import { TasksPage } from "@/pages/tasks/TasksPage"
import { ProgressPage } from "@/pages/progress/ProgressPage"
import { ReviewsPage } from "@/pages/ReviewsPage"
import { ReputationPage } from "@/pages/reputation/ReputationPage"
import { ListingIntelligencePage } from "@/pages/listings/ListingIntelligencePage"
import { UsagePage } from "@/pages/UsagePage"
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage"
import { CaseStudyPage } from "@/pages/CaseStudyPage"
import { DemoLockedPage } from "@/components/demo/DemoLockedPage"
import DemoEntryPage from "@/pages/DemoEntryPage"
import { usePropertyContext } from "@/lib/usePropertyContext"
import type { LucideIcon } from "lucide-react"
import { MapPin, TrendingUp, ScrollText, Monitor, Settings, AlertTriangle, RefreshCw } from "lucide-react"
import { Component, type ErrorInfo, type ReactNode } from "react"

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="sf-spinner" />
      </div>
    )
  }

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }

  return <>{children}</>
}

function RequireProperty({ children }: { children: React.ReactNode }) {
  const { properties, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="sf-spinner" />
      </div>
    )
  }

  if (properties.length === 0) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const { isPlatformAdmin, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="sf-spinner" />
      </div>
    )
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function RequireOpsAdmin({ children }: { children: React.ReactNode }) {
  const { isOpsAdmin, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="sf-spinner" />
      </div>
    )
  }

  if (!isOpsAdmin) {
    return <Navigate to="/properties" replace />
  }

  return <>{children}</>
}

function DemoGuard({
  children,
  title,
  description,
  previewHint,
  icon,
}: {
  children: React.ReactNode
  title: string
  description: string
  previewHint: string
  icon?: LucideIcon
}) {
  const { isDemo } = usePropertyContext()
  if (isDemo) return <DemoLockedPage title={title} description={description} previewHint={previewHint} icon={icon} />
  return <>{children}</>
}

// ─── Route-level Error Boundary ──────────────────────────────────────────────
// Catches render errors in any page that doesn't have its own error boundary.

interface PageErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class PageErrorBoundary extends Component<{ children: ReactNode }, PageErrorBoundaryState> {
  state: PageErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[PageErrorBoundary] render failed", error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive mb-3" />
          <h2 className="text-lg font-semibold mb-1">Something went wrong</h2>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            This page encountered an unexpected error. Try refreshing, or go back to the overview.
          </p>
          <div className="flex gap-2">
            <button
              className="sf-btn sf-btn--primary sf-btn--sm"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Try again
            </button>
            <a href="/properties" className="sf-btn sf-btn--ghost sf-btn--sm">
              Go to properties
            </a>
          </div>
          {this.state.error && (
            <pre className="mt-4 text-xs text-muted-foreground bg-muted p-3 rounded-sm max-w-lg overflow-auto max-h-[120px]">
              {this.state.error.message}
            </pre>
          )}
        </div>
      )
    }
    return this.props.children
  }
}

function PortfolioRoute() {
  const { properties } = useAuth()
  // Single-property hosts skip the portfolio cockpit and land in their property.
  if (properties.length === 1) {
    return <Navigate to={`/properties/${properties[0].id}`} replace />
  }
  const subtitle = properties.length > 0 ? `${properties.length} properties` : undefined
  return (
    <AppShell mode="portfolio" propertyId={null} title="Portfolio" subtitle={subtitle}>
      <PageErrorBoundary>
        <PortfolioPage />
      </PageErrorBoundary>
    </AppShell>
  )
}

export function App() {
  return (
    <Routes>
      <Route path="/demo" element={<DemoEntryPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sign-up" element={<SignUpPage />} />
      <Route path="/check-inbox" element={<CheckInboxPage />} />
      <Route path="/auth/activate" element={<ActivateAccountPage />} />
      <Route path="/email-confirmed" element={<EmailConfirmedPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/case-study" element={<CaseStudyPage />} />
      <Route path="/g/:key" element={<GuidebookPage />} />
      <Route path="/p/:slug" element={<GuestGuidebookPage />} />
      <Route path="/p/:slug/guide" element={<GuestGuidebookPage />} />
      <Route path="/p/:slug/tv" element={<TvDisplayPage />} />
      <Route path="/p/:slug/review-guide" element={<ReviewGuidePage />} />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <OnboardingPage />
          </RequireAuth>
        }
      />

      {/* Platform admin */}
      <Route
        path="/platform/*"
        element={
          <RequireAuth>
            <RequirePlatformAdmin>
              <PlatformShell />
            </RequirePlatformAdmin>
          </RequireAuth>
        }
      >
        <Route index element={<PlatformOverviewPage />} />
        <Route path="users" element={<PlatformUsersPage />} />
        <Route path="properties" element={<PlatformPropertiesPage />} />
        <Route path="*" element={<Navigate to="/platform" replace />} />
      </Route>

      {/* Internal ops admin */}
      <Route
        path="/admin/*"
        element={
          <RequireAuth>
            <RequireOpsAdmin>
              <OpsShell />
            </RequireOpsAdmin>
          </RequireAuth>
        }
      >
        <Route index element={<OpsAdminPage />} />
        <Route path="ai" element={<OpsAiRoutingPage />} />
        <Route path="costs" element={<OpsCostBreakdownPage />} />
        <Route path="users" element={<OpsUsersPage />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>

      {/* Property workspace — route-driven property context */}
      <Route
        path="/properties/:propertyId"
        element={
          <RequireAuth>
            <RequireProperty>
              <ValidatePropertyAccess>
                <PropertyWorkspaceLayout />
              </ValidatePropertyAccess>
            </RequireProperty>
          </RequireAuth>
        }
      >
        <Route index element={<PageErrorBoundary><OverviewSurface /></PageErrorBoundary>} />
        <Route path="performance" element={<PageErrorBoundary><RevenuePage /></PageErrorBoundary>} />
        <Route path="listings" element={<PageErrorBoundary><ListingIntelligencePage /></PageErrorBoundary>} />
        <Route path="calendar" element={<Navigate to="../settings" replace relative="path" />} />
        <Route path="places" element={
          <Navigate to="../guest-experience/places" replace relative="path" />
        } />
        <Route path="progress" element={
          <PageErrorBoundary>
            <DemoGuard title="Progress" description="Track your property's setup journey. See what's configured, what's pending, and get step-by-step guidance to complete your profile." previewHint="Your setup checklist and completion tracker — connect to begin." icon={TrendingUp}>
              <ProgressPage />
            </DemoGuard>
          </PageErrorBoundary>
        } />
        <Route path="research" element={<Navigate to="../guest-experience/places" replace relative="path" />} />
        <Route path="jobs" element={<Navigate to=".." replace relative="path" />} />
        <Route path="audit" element={<Navigate to=".." replace relative="path" />} />
        <Route path="settings" element={
          <PageErrorBoundary>
            <DemoGuard title="Settings" description="Manage your property configuration, billing, team members, and integrations. All your property controls in one place." previewHint="Property settings and team management — connect to configure." icon={Settings}>
              <SettingsPage />
            </DemoGuard>
          </PageErrorBoundary>
        } />
        <Route path="house-rules" element={<Navigate to="../guest-experience/rules" replace relative="path" />} />
        <Route path="reviews" element={<PageErrorBoundary><ReputationPage /></PageErrorBoundary>} />
        <Route path="tasks" element={<PageErrorBoundary><TasksPage /></PageErrorBoundary>} />
        <Route path="display-behaviour" element={<Navigate to="../guest-experience/display" replace relative="path" />} />
        <Route path="usage" element={<PageErrorBoundary><UsagePage /></PageErrorBoundary>} />
        <Route path="guidebook" element={<Navigate to="../guest-experience" replace relative="path" />} />
        {/* Guest Experience — tabbed surface consolidating guidebook, display, places, house rules */}
        <Route path="guest-experience" element={<PageErrorBoundary><GuestExperiencePage /></PageErrorBoundary>}>
          <Route index element={<GuidebookTab />} />
          <Route path="display" element={
            <DemoGuard title="Display" description="Control how your property appears across guest-facing surfaces. Customise your guidebook layout, TV display, and public profile." previewHint="Guest-facing display controls — connect to customise." icon={Monitor}>
              <InRoomDisplayTab />
            </DemoGuard>
          } />
          <Route path="places" element={
            <DemoGuard title="Places" description="Your local recommendations, powered by AI research. Curated restaurants, beaches, activities, and more — all tailored to your property's location." previewHint="50+ curated places for your guests — connect to start building." icon={MapPin}>
              <RecommendationsTab />
            </DemoGuard>
          } />
          <Route path="rules" element={
            <DemoGuard title="House Rules" description="Define and manage your property's house rules. Smart formatting ensures guests see clear, well-structured guidelines." previewHint="AI-formatted house rules for your guests — connect to set up." icon={ScrollText}>
              <HouseRulesTab />
            </DemoGuard>
          } />
        </Route>
        <Route path="*" element={<Navigate to="" replace />} />
      </Route>

      {/* Portfolio — multi-property cockpit (new landing surface) */}
      <Route
        path="/properties"
        element={
          <RequireAuth>
            <RequireProperty>
              <PortfolioRoute />
            </RequireProperty>
          </RequireAuth>
        }
      />

      {/* Legacy flat routes → redirect to property workspace */}
      <Route
        path="/*"
        element={
          <RequireAuth>
            <RequireProperty>
              <LegacyRouteRedirect />
            </RequireProperty>
          </RequireAuth>
        }
      />
    </Routes>
  )
}
